import axios from "axios";
import { DateTime } from "luxon";

import {
    Address,
    Appointment, AssignmentGroupDetails, AuthData, ContactDetails, Date, DTime, Encounter, Insurance, LoginParams, LoginResult, Office, Patient,
    Place, Practice, PracticeService, RefreshTokenParams, RequestCodeParams, RequestCodeResult,
    Role, ScheduleStaffPair, ScheduleWindowPair, Service, User
} from "../models/ziphy";


export interface Filter {
    and?: Filter[];
    or?: Filter[];
    eq?: [string, unknown];
    ne?: [string, unknown];
    gte?: [string, unknown];
    lte?: [string, unknown];
    imatch?: [string, unknown];
    in?: [string, unknown];
    contains?: [string, unknown];
    contained?: [string, unknown];
}

export interface List<T> {
    count: number;
    items: T[];
}

export interface Mapping<T, K = number> {
    items: { key: K; value: T }[];
}

interface ListPracticesResult {
    practices: List<Practice>;
}

interface ReadPracticesResult {
    practice: Practice;
}

export interface ListPracticeServicesResult {
    practice_services: List<PracticeService>;
    expanded: {
        services: Mapping<Service>;
    }
}

export interface ListPatientsResult {
    patients: List<Patient>;
}

export interface ReadPatientResult {
    patient:Patient;
}

export interface ReadPatientExpandedResult {
    patient:Patient;
    expanded: {
        places: Mapping<Place>;
    }
}

export interface ReadScheduleWindowResult {
    windows: {
        items:List<ScheduleWindowPair>;
        start?:DTime;
        end?:DTime;
    }
    staff:{
        items:List<ScheduleStaffPair>
    };
}

interface ListAppointmentsResult {
    appointments: List<Appointment>;
    expanded?: {
        patients: Mapping<Patient>;
        places: Mapping<Place>;
    }
}

interface ListAppointmentsEncountersResult {
    appointments: List<Appointment>;
    expanded?: {
        patients: Mapping<Patient>;
        places: Mapping<Place>;
        encounters: Mapping<Encounter>
    }
}

interface ReadAppointmentResult {
    appointment: Appointment;
    expanded: {
        patients: Mapping<Patient>;
        places: Mapping<Place>;
    }
}

interface ListPlacesResult {
    places: List<Place>;
    expanded: {
        users: Mapping<User>;
    }
}

interface ReadPlacesResult {
    place: Place;
}

interface ListAddressesResult {
    addresses: List<Address>;
}

interface ListInsuranceResult {
    insurances: List<Insurance>;
}

interface ListContactDetailsResult {
    contacts: List<ContactDetails>;
}

interface ListRolesResult {
    roles: List<Role>;
    expanded: {
        users: Mapping<User>;
        offices: Mapping<Office>;
        practices: Mapping<Practice>;
    }
}

interface ListOfficesResult {
    offices: List<Office>;
    // expanded?: {
    //     patients: Mapping<Patient>;
    //     places: Mapping<Place>;
    // }
}

interface ListAssignmentGroupDetailsResult {
    assignment_details: List<AssignmentGroupDetails>;
}

interface SlotsRequest {
    begin: Date;
    service_id: number;
    place_id: number;
    patient_number: number;
    symptom_ids: number[];
    practice_id: number;
    office_id: number | null;
}

interface BookAppointmentRequest {
    practice_id: number;
    service_id: number;
    place_id: number;
    patients: { [k: string]: unknown }[];
    start: DTime;
    symptom_ids: number[];
    answers: { [k: number]: { [k: number]: number } };
    agent_role_id?: number;
    provider_role_id?: number;
    office_id?: number;
    optimistic?: boolean;
    description: string;
}

export interface SlotsResult {
    windows: {
        begin: DTime;
        end: DTime;
        items: Mapping<number[], DTime> | Record<string,never>;
    }
}

export interface ScheduleReportResponse {
    working_ranges: {
        begin: DTime;
        end: DTime;
        map: Mapping<{begin: DTime;end: DTime;}[]>;
    },
    role_map: Mapping<{name: string, role_ids: number[]}>;
}

interface Response<T> {
    result: T;
    error: {
        code: string,
        message: string,
        data: {
            reason: { [k: string]: string },
            method: string
        }
    } | null;
}


 export interface InsuranceProvider {
    id:number;
    name:string;
    payer_id:string;
}

interface ListInsuranceProviderResult {
    count: number;
    limit: number;
    offset: number;
    insurance_providers: List<InsuranceProvider>;
}

class _API {
    baseUrl: string;
    requestId: number;
    token: string;
    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
        this.requestId = 0;
        this.token = '';
    }

    requestCode = async (params: RequestCodeParams) => (
        await this.post<RequestCodeResult, RequestCodeParams>(
            'user.auth.request_code', 
            params,
            { access_token: undefined }
        )
    );

    login = async (params: LoginParams): Promise<AuthData> => {
        const result = await this.post<LoginResult, LoginParams>(
            'user.auth.login', 
            params,
            { access_token: undefined}
        );
        return {
            user: result.user,
            role: result.role,
            roles: result.roles?.items,
            account: result.account,
            accessToken: {
                value: result.access_token,
                expiresAt: DateTime.now().plus({ second: result.access_token_timeout }).toISO() || ''
            },
            sessionToken: {
                value: result.session_token,
                expiresAt: DateTime.now().plus({ second: result.session_token_timeout }).toISO() || ''
            },
        };
    };

    refreshToken = async (params: RefreshTokenParams): Promise<AuthData> => {
        const result = await this.post<LoginResult, RefreshTokenParams>(
            'user.auth.refresh', 
            params,
            { access_token: undefined}
        );
        return result && {
            user: result.user,
            role: result.role,
            roles: result.roles?.items,
            account: result.account,
            accessToken: {
                value: result.access_token,
                expiresAt: DateTime.now().plus({ second: result.access_token_timeout }).toISO() || ''
            },
            sessionToken: {
                value: result.session_token,
                expiresAt: DateTime.now().plus({ second: result.session_token_timeout }).toISO() || ''
            },
        };
    };

    getPractices = async () => (
        await this.post<ListPracticesResult, unknown>(
            'practices.list',
            {
                /*filter: {
                    and: [
                        { in: ["id", [59]] }
                    ]
                },*/
                limit: 1000
            }
        )).practices.items;

    getPractice = async (practiceId:number) => (
        await this.post<ReadPracticesResult, unknown>(
            'practices.read',
            {   id:practiceId  }
        )).practice;

    getPracticeServices = async (practice_id: number) => (
        await this.post<ListPracticeServicesResult, unknown>(
            'practice_services.list',
            {
                filter: {
                    and: [
                        { eq: ["practice_id", practice_id] },
                        { eq: ["is_active", true] }
                    ]
                }, limit: 1000
            },
            { expand: { practice_service: ['service_id'] } }
        ));


    getPatients = async (filter: Filter) => (
        await this.post<ListPatientsResult, unknown>(
            'patients.list',
            { filter }
        ))


    getPatient = async (id:number) => (
        await this.post<ReadPatientResult, unknown>(
            'patients.read',
            { id:id },
        ));

    getPatientExpanded = async (id:number) => (
        await this.post<ReadPatientExpandedResult, unknown>(
            'patients.read',
            { id:id },
            { expand: { patient: ['place_id'] } }
        ));

    createPatient = async (patient: Omit<Patient, 'id'>, practice_id: number) =>
        (await this.post<{patient: Patient}, (Omit<Patient, 'id'> & { practice_id: number })>(
            'patients.create',
            { ...patient, practice_id }
        )).patient

    updatePatient = async (patient: Partial<Patient>) =>
        (await this.post<{patient: Patient}, (Partial<Patient>)>(
            'patients.update',
            { ...patient }
        )).patient

    getAppointment = async (id: number) => (
        await this.post<ReadAppointmentResult, unknown>(
            'appointments.read',
            { id: id },
            { expand: { appointment: ['patient_id', 'agent_id'] } }
        ));

    getAppointments = async (filter: Filter) => (
        await this.post<ListAppointmentsResult, unknown>(
            'appointments.list',
            { filter: filter, limit: 1000 },
            { expand: { appointment: ['patient_id', 'agent_id'] } }
        ));

    getAppointmentsWithEncounters = async (filter: Filter) => (
        await this.post<ListAppointmentsEncountersResult, unknown>(
            'appointments.list',
            { filter: filter, limit: 1000 },
            { expand: { appointment: ['patient_id', 'agent_id', 'encounter_ids'], encounter:['patient_id'] } }
        ));

    bookAppointment = async (request: BookAppointmentRequest) => (
        (await this.post<{appointment: Appointment}, BookAppointmentRequest>(
            'appointments.create',
            request
        )).appointment
    )


    getRoles = async (filter: Filter) => (
        await this.post<ListRolesResult, unknown>(
            'roles.list',
            { filter: filter, limit: 1000 },
            { expand: { role: ['user_id', 'office_id', 'practice_id'] } }
        )
    );

    getPlaces = async (filter: Filter) => (
        await this.post<ListPlacesResult, unknown>(
            'places.list',
            { filter: filter, limit: 1000 },
            { expand: { place: ['user_id'] } }
        )
    );

    getPlace = async (place_id: number) => (
        await this.post<ReadPlacesResult, unknown>(
            'places.read',
            { id: place_id }
        )
    );

    getAddresses = async (patientId:number) => (
        await this.post<ListAddressesResult, unknown>(
            'patient_profiles.addresses.list',
            { patient_id: patientId },
        )
    );

    createAddresses = async (patientId:number, address:Omit<Address, 'id'>) => (
        await this.post<ListAddressesResult, unknown>(
            'patient_profiles.addresses.create',
            { patient_id: patientId, ...address },
        )
    );


    createContact = async (patientId:number, params:{system:'phone', use:'home', value:string}) => (
        await this.post<ListAddressesResult, unknown>(
            'patient_profiles.contacts.create',
            { patient_id:patientId, ...params},
        )
    );

    createPlace = async (place: Omit<Place, 'id'>, practice_id: number) =>
        (await this.post<{place: Place}, (Omit<Place, 'id'> & { practice_id: number })>(
           'places.create',
            { ...place, practice_id }
        )).place

    getOffices = async (filter: Filter) => (
        await this.post<ListOfficesResult, unknown>(
            'offices.list',
            { filter: filter, limit: 1000 }
        )
    );

    getSlots = async (
        practice_id: number, place_id: number, service_id: number,
        office_id: number, begin: Date) => (
        await this.post<SlotsResult, SlotsRequest>(
            'schedules.find_windows',
            {
                practice_id, place_id, service_id, begin, office_id,
                patient_number: 1, symptom_ids: []
            },
            { expand: {} }
        )
    );

    getSchedulesReport = async (
        practice_id: number, office_id: number, begin: DTime, end: DTime
    ) => (
        await this.post<ScheduleReportResponse, unknown>(
            'schedules.report',
            {
                begin, end,
                filter: {
                    and: [
                    { eq:['practice_id', practice_id]},
                    {
                    or: [
                        { eq: ["office_id", office_id] },
                        { is_none: "office_id" }
                    ]
                    }
                ]
                },
            }
        )
    )

    getSchedulableStaff = async(begin: DTime, end: DTime, officeId:number, optimistic:boolean, 
        placeId:number, serviceId:number, patientNumber:number) => {
            return await this.post<ReadScheduleWindowResult, unknown>('schedules.find_staff',
                {
                    begin:begin,
                    end:end,
                    office_id:officeId,
                    optimistic:optimistic,
                    patient_number:patientNumber,
                    place_id:placeId,
                    service_id:serviceId
                }
            )
    }

    getPatientContactInfo = async (zcmId: number) => (
        await this.post<ListContactDetailsResult, unknown>(
            'patient_profiles.contacts.list',
            {patient_id:zcmId}
        )
    );

    getInsuranceList = async (patientId: number) => (
        await this.post<ListInsuranceResult, unknown>(
            'patient_profiles.insurances.list',
            //{ filter: filter, limit: 1000 }
            {patient_id:patientId}
        )
    );

    createInsurance = async(insurance:Insurance) => (
        await this.post(
            'patient_profiles.insurances.create',
            {...insurance}
        )
    )

    getAssignmentGrounpDetails = async (filter: Filter, limit=25) => (
        await this.post<ListAssignmentGroupDetailsResult, unknown>(
            'assignments.detailed_list',
            { filter: filter, limit:limit }
        )
    );

    getDocumentTypes = async () => (
        await this.postNoExpand<Record<string,string>, unknown>(
            'documents.list_document_types',
            { include_internal: false }
        )
    );

    getInsuranceProviderList = async (offset = 0, limit = 1000) => {
        return await this.postNoExpand<ListInsuranceProviderResult, unknown>(
            'insurance_providers.list',
            { limit:limit, offset:offset }
        );
    }

    getDocumentUploadLink = async (contentType:string, fileName:string, encounterId:number) => {
        return await this.post<unknown, unknown>(
            'documents.get_documents_upload_urls',
            {
                data: [
                    {
                        content_type:contentType,
                        filename:fileName,
                    },
                ],
                encounter_id:encounterId
            }
        )
    }

    async post<T, U = T>(path: string, data: U, meta?: { [k: string]: unknown }): Promise<T> {
        this.requestId += 1;
        const response = await axios.post<Response<T>>(
            this.baseUrl + '?' + path,
            {
                "id": this.requestId,
                "method": path,
                meta: { 
                    version: [3, 0], 
                    expand: { role: ['practice_id'] }, 
                    access_token: this.token,
                    ...meta,  
                },
                "params": data
            });
        if (typeof response.data === 'string') {
            return JSON.parse(response.data) as T;
        }
        if (response.data.result) {
            return response.data.result;
        }
        if (response.data.error) {
            console.error(response.data.error);
            throw Error('Ziphy API error: ' + JSON.stringify(response.data.error), {cause:response.data.error});
        }
        throw Error('Neither result, nor error', { cause: response.data })
    }

    async postNoExpand<T, U = T>(path: string, data: U, meta?: { [k: string]: unknown }): Promise<T> {
        this.requestId += 1;
        const response = await axios.post<Response<T>>(
            this.baseUrl + '?' + path,
            {
                "id": this.requestId,
                "method": path,
                meta: { 
                    version: [3, 0], 
                    access_token: this.token,
                    ...meta,  
                },
                "params": data
            });
        if (typeof response.data === 'string') {
            return JSON.parse(response.data) as T;
        }
        if (response.data.result) {
            return response.data.result;
        }
        if (response.data.error) {
            console.error(response.data.error);
            throw Error('Ziphy API error: ' + JSON.stringify(response.data.error), {cause:response.data.error});
        }
        throw Error('Neither result, nor error', { cause: response.data })
    }
}

export const API = new _API(process.env.REACT_APP_ZIPHY_API_ENDPOINT || 'http://localhost:5000/myack-rpc/');
